// Marketing landing page for unauthenticated visitors, modeled on the
// try-before-signup funnel: the hero IS an interactive generator (I2V/T2V
// tabs). Clicking Generate stashes the draft and opens the signup-nudge
// dialog; dismissing it creates a fingerprint-keyed guest account and lands
// on /minimax-h3 with the draft prefilled. Below the fold: use-cases and the
// full feature grid. All copy comes from the `landing` i18n namespace.

import { Box, Button, Container, Paper, Stack, Typography } from '@mui/material';
import { BrandMark } from '../components/BrandMark';
import VideocamIcon from '@mui/icons-material/VideocamOutlined';
import PublicIcon from '@mui/icons-material/PublicOutlined';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHighOutlined';
import ImagePlayIcon from '@mui/icons-material/SlideshowOutlined';
import DrawIcon from '@mui/icons-material/DrawOutlined';
import WidgetsIcon from '@mui/icons-material/WidgetsOutlined';
import MovieIcon from '@mui/icons-material/MovieFilterOutlined';
import EditIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ImageIcon from '@mui/icons-material/ImageOutlined';
import ViewInArIcon from '@mui/icons-material/ViewInArOutlined';
import FaceIcon from '@mui/icons-material/FaceRetouchingNaturalOutlined';
import PortraitIcon from '@mui/icons-material/PortraitOutlined';
import CameraAltIcon from '@mui/icons-material/CameraAltOutlined';
import LocaleSwitcher from '../i18n/LocaleSwitcher';
import { useAuth } from '../auth/AuthProvider';
import { format, useTranslation } from '../i18n/I18nProvider';
import { SignupNudgeProvider, useSignupNudge } from '../components/SignupNudge';
import { PROMO_CREDITS } from '../lib/promo';
import HeroGenerator from './landing/HeroGenerator';
import type { TranslationKeys } from '../i18n/translations/en';
import type { ReactNode } from 'react';

export default function Landing() {
  return (
    <SignupNudgeProvider>
      <LandingContent />
    </SignupNudgeProvider>
  );
}

// Feature-namespace keys that carry a navTitle + description pair.
type FeatureNs = Pick<
  TranslationKeys,
  | 'minimaxH3'
  | 'imageEdit'
  | 'multiAngle'
  | 'faceSwap'
  | 'textToImage'
  | 'humanPortrait'
  | 'instagramGenerator'
>;

const FEATURES: Array<{ ns: keyof FeatureNs; icon: ReactNode }> = [
  { ns: 'minimaxH3', icon: <MovieIcon /> },
  { ns: 'imageEdit', icon: <EditIcon /> },
  { ns: 'multiAngle', icon: <ViewInArIcon /> },
  { ns: 'faceSwap', icon: <FaceIcon /> },
  { ns: 'textToImage', icon: <ImageIcon /> },
  { ns: 'humanPortrait', icon: <PortraitIcon /> },
  { ns: 'instagramGenerator', icon: <CameraAltIcon /> },
];

function LandingContent() {
  const { signIn, signUp } = useAuth();
  const t = useTranslation();
  const { open } = useSignupNudge();
  const returnTo = '/minimax-h3';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Stack
        component="header"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: { xs: 3, md: 6 }, py: 2.5 }}
      >
        <BrandMark size={20} />
        <Stack direction="row" spacing={1.5} alignItems="center">
          <LocaleSwitcher />
          <Button size="small" variant="text" onClick={() => signIn(returnTo)}>
            {t.landing.signIn}
          </Button>
          <Button size="small" variant="contained" onClick={() => signUp(returnTo)}>
            {t.landing.signUp}
          </Button>
        </Stack>
      </Stack>

      {/* Hero: headline + interactive generator */}
      <Container maxWidth="md" sx={{ pt: { xs: 5, md: 8 }, pb: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: 34, md: 48 },
            fontWeight: 800,
            letterSpacing: '-0.03em',
            lineHeight: 1.08,
            mb: 2,
          }}
        >
          {t.landing.heroTitle}
        </Typography>
        <Typography
          variant="h2"
          sx={{ fontSize: { xs: 16, md: 20 }, fontWeight: 500, color: 'text.secondary', mb: 3 }}
        >
          {t.landing.heroSubtitle}
        </Typography>

        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'rgba(124, 111, 240, 0.14)',
            color: '#b9b0ff',
            px: 2,
            py: 0.75,
            borderRadius: 999,
            fontSize: 14,
            fontWeight: 600,
            mb: 4,
          }}
        >
          {format(t.landing.promoBanner, {
            signup: PROMO_CREDITS.SIGNUP,
            email: PROMO_CREDITS.EMAIL_VERIFY,
          })}
        </Box>

        <HeroGenerator onGenerate={() => open('landing')} />
      </Container>

      {/* Use cases */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 }, textAlign: 'center' }}>
        <Typography variant="h3" sx={{ fontSize: { xs: 24, md: 32 }, fontWeight: 800, mb: 5 }}>
          {t.landing.useCasesTitle}
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={4}
          justifyContent="center"
          alignItems={{ xs: 'center', sm: 'flex-start' }}
        >
          <Blurb icon={<ImagePlayIcon fontSize="large" />} title={t.landing.useCase1Title} body={t.landing.useCase1Body} />
          <Blurb icon={<DrawIcon fontSize="large" />} title={t.landing.useCase2Title} body={t.landing.useCase2Body} />
          <Blurb icon={<WidgetsIcon fontSize="large" />} title={t.landing.useCase3Title} body={t.landing.useCase3Body} />
        </Stack>
      </Container>

      {/* Feature grid */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Typography
          variant="h3"
          sx={{ fontSize: { xs: 24, md: 32 }, fontWeight: 800, mb: 1, textAlign: 'center' }}
        >
          {t.landing.featuresTitle}
        </Typography>
        <Typography sx={{ color: 'text.secondary', mb: 5, textAlign: 'center' }}>
          {t.landing.featuresSubtitle}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 2.5,
          }}
        >
          {FEATURES.map(({ ns, icon }) => (
            <Paper
              key={ns}
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 2.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                transition: 'border-color 120ms',
                '&:hover': { borderColor: 'primary.main' },
              }}
            >
              <Box sx={{ color: 'primary.main' }}>{icon}</Box>
              <Typography sx={{ fontWeight: 700 }}>{t[ns].navTitle}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', flex: 1 }}>
                {t[ns].description}
              </Typography>
              <Button
                size="small"
                onClick={() => open('landing')}
                sx={{ alignSelf: 'flex-start', px: 0 }}
              >
                {t.landing.featureCta} →
              </Button>
            </Paper>
          ))}
        </Box>
      </Container>

      {/* Footer value props */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={4}
          justifyContent="center"
          alignItems={{ xs: 'center', sm: 'flex-start' }}
        >
          <Blurb icon={<VideocamIcon fontSize="large" />} title="LTX 2.3 & Wan 2.2" body={t.landing.footerModels} />
          <Blurb icon={<PublicIcon fontSize="large" />} title="Open source" body={t.landing.footerOpenSource} />
          <Blurb icon={<AutoFixHighIcon fontSize="large" />} title="Uncensored" body={t.landing.footerUncensored} />
        </Stack>
      </Container>
    </Box>
  );
}

function Blurb({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <Box sx={{ maxWidth: 300, textAlign: 'center' }}>
      <Box sx={{ color: 'primary.main', mb: 1.5 }}>{icon}</Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {body}
      </Typography>
    </Box>
  );
}
